'use client';
import React, { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { useAppSelector, useAppStore } from '@/lib/hooks';

import RouteGuardCallbacks from '@/services/nav/route-guard-callbacks';
import { routeGuardCallbackId } from 'auth-types';

export default function RouteChangeGuard() {
  const router = useRouter();
  const store = useAppStore();

  const isGuardActive: boolean = useAppSelector(() => store.getState().routeGuard.guardActive);
  const guardMessage: string = useAppSelector(() => store.getState().routeGuard.guardMessage);
  const unloadCallbackId: undefined | routeGuardCallbackId = useAppSelector(
    () => store.getState().routeGuard.unloadCallback,
  );
  const criticalData: undefined | unknown = useAppSelector(
    () => store.getState().routeGuard.criticalData,
  );

  const reduxStore: any = useAppSelector(() => store.getState());
  const pathname = usePathname();

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (isGuardActive) {
      event.preventDefault();
      if (unloadCallbackId) navigationCallback(criticalData);
      event.returnValue = '';
    }
  };

  const handlePopState = () => {
    if (isGuardActive) {
      if (unloadCallbackId) navigationCallback(criticalData);

      const confirmNavigation = confirm(guardMessage);
      if (!confirmNavigation) {
        history.pushState(null, '', pathname);
      }
    }
  };

  const interceptNavigation = () => {
    if (isGuardActive) {
      const confirmNavigation = confirm(guardMessage);
      if (!confirmNavigation) {
        throw new Error('Navigation aborted by user.');
      } else if (unloadCallbackId) {
        navigationCallback(criticalData);
      }
    }
  };

  const navigationCallback = (currentCriticalData: any) => {
    switch (unloadCallbackId) {
      case 'IncompleteQuizNav':
        RouteGuardCallbacks.incompleteQuizCallback(
          reduxStore?.course?.activeCourseData?.id,
          reduxStore?.unit?.id,
          reduxStore?.subunit?.id,
          reduxStore?.activity?.id,
          currentCriticalData,
        );
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    // Patch router's push method to intercept navigation (iternal routing)
    const originalPush = router.push;
    router.push = async (...args: Parameters<typeof originalPush>) => {
      try {
        interceptNavigation();
        return originalPush(...args);
      } catch (error) {
        console.error('Navigation aborted.');
        return;
      }
    };

    // Handle browser unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      // Restore the original push (iternal routing cleanup)
      router.push = originalPush;

      // Cleanup listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [pathname, isGuardActive, guardMessage, unloadCallbackId, criticalData]);

  return <></>;
}
